import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { getProducts, getBrands, getSpecValues, getYears } from '../services/api'
import { PageShell } from '../components/page/PageShell'
import { ProductCard } from '../components/products/ProductCard'

const PER_PAGE = 24
const CATEGORY_BAT = 'padel_bat'

function labelSpec(value) {
  if (!value) return ''
  const v = value.toLowerCase()
  const map = {
    round: 'Rund',
    diamond: 'Diamant',
    teardrop: 'Dråbe',
    hybrid: 'Hybrid',
    soft: 'Blød',
    medium: 'Medium',
    hard: 'Hård',
    'head heavy': 'Head heavy',
    balance: 'Balanceret',
    even: 'Lige',
  }
  return map[v] || value.charAt(0).toUpperCase() + value.slice(1)
}

function BatListPage() {
  const [searchParams] = useSearchParams()
  const [yearFilter, setYearFilter] = useState('')
  const [brandFilter, setBrandFilter] = useState('')
  const [shapeFilter, setShapeFilter] = useState('')
  const [coreFilter, setCoreFilter] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    const brand = searchParams.get('brand')
    const year = searchParams.get('year')
    if (brand) setBrandFilter(brand)
    if (year) setYearFilter(year)
    setPage(1)
  }, [searchParams])

  const { data: brands = [] } = useQuery({
    queryKey: ['brands', CATEGORY_BAT],
    queryFn: () => getBrands(CATEGORY_BAT),
    retry: 1,
  })
  const { data: shapeValues = [] } = useQuery({
    queryKey: ['spec-values', CATEGORY_BAT, 'shape'],
    queryFn: () => getSpecValues(CATEGORY_BAT, 'shape'),
    retry: 1,
  })
  const { data: coreValues = [] } = useQuery({
    queryKey: ['spec-values', CATEGORY_BAT, 'core'],
    queryFn: () => getSpecValues(CATEGORY_BAT, 'core'),
    retry: 1,
  })
  const { data: yearsList = [] } = useQuery({
    queryKey: ['years', CATEGORY_BAT],
    queryFn: () => getYears(CATEGORY_BAT),
    retry: 1,
  })

  const hasOlder = yearsList.some((y) => y <= 2023)
  const yearOptions = [
    { value: '', label: 'Alle år' },
    ...yearsList.map((y) => ({ value: String(y), label: String(y) })),
    ...(hasOlder ? [{ value: 'older', label: 'Ældre (2023 og tidligere)' }] : []),
  ]

  const params = {
    category: CATEGORY_BAT,
    published_only: true,
    per_page: PER_PAGE,
    page,
  }
  if (yearFilter === 'older') {
    params.year_max = 2023
  } else if (yearFilter) {
    params.year = parseInt(yearFilter, 10)
  }
  params.sort = 'year'
  if (brandFilter) params.brand = brandFilter
  if (shapeFilter) params.shape = shapeFilter
  if (coreFilter) params.core = coreFilter

  const { data, isLoading, error } = useQuery({
    queryKey: ['products', 'bat-list', params],
    queryFn: () => getProducts(params),
    retry: 2,
  })

  const products = data?.products || []
  const total = data?.total || 0
  const pages = data?.pages || 1
  const title = brandFilter ? `${brandFilter} padel bats` : 'Padel bats'

  return (
    <PageShell
      backTo="/"
      backLabel="Hjem"
      breadcrumbs={[
        { label: 'Hjem', to: '/' },
        { label: title },
      ]}
      actions={[
        { label: 'Søg', to: '/search' },
      ]}
      title={title}
      intro="Komplet oversigt over padel bats. Filtrer efter brand, udgivelsesår, form og skumhårdhed. Listen er sorteret efter kollektion (nyest først)."
    >
      <div className="page-filters">
        <label>
          <span className="filter-label">Brand</span>
          <select
            value={brandFilter}
            onChange={(e) => {
              setBrandFilter(e.target.value)
              setPage(1)
            }}
            className="filter-select"
          >
            <option value="">Alle brands</option>
            {brands.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="filter-label">Udgivelsesår</span>
          <select
            value={yearFilter}
            onChange={(e) => {
              setYearFilter(e.target.value)
              setPage(1)
            }}
            className="filter-select"
          >
            {yearOptions.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="filter-label">Form</span>
          <select
            value={shapeFilter}
            onChange={(e) => {
              setShapeFilter(e.target.value)
              setPage(1)
            }}
            className="filter-select"
          >
            <option value="">Alle former</option>
            {shapeValues.map((v) => (
              <option key={v} value={v}>
                {labelSpec(v)}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="filter-label">Skumhårdhed</span>
          <select
            value={coreFilter}
            onChange={(e) => {
              setCoreFilter(e.target.value)
              setPage(1)
            }}
            className="filter-select"
          >
            <option value="">Alle</option>
            {coreValues.map((v) => (
              <option key={v} value={v}>
                {labelSpec(v)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isLoading ? (
        <div className="page-loading">Indlæser...</div>
      ) : error ? (
        <div className="page-error">
          <p>Fejl ved indlæsning: {error.message}</p>
        </div>
      ) : products.length === 0 ? (
        <div className="page-empty">
          Ingen bats fundet med valgte filter. Prøv et andet år eller <Link to="/search">søg</Link>.
        </div>
      ) : (
        <>
          <p className="page-result-count">
            Viser {products.length} af {total} bat{total !== 1 ? 's' : ''}
          </p>
          <div className="product-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} summaryMax={90} />
            ))}
          </div>
          {pages > 1 && (
            <nav className="pagination">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="pagination-btn"
              >
                Forrige
              </button>
              <span className="pagination-info">
                Side {page} af {pages}
              </span>
              <button
                type="button"
                disabled={page >= pages}
                onClick={() => setPage((p) => p + 1)}
                className="pagination-btn"
              >
                Næste
              </button>
            </nav>
          )}
        </>
      )}
    </PageShell>
  )
}

export default BatListPage
